import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "../api";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";

const CARE_TYPES = ["Personal Care", "Companionship", "Respite Care", "Meal Preparation", "Overnight Care", "Dementia Support", "Post-Hospital Support", "Transportation", "Other"];
const VISIT_STATUSES = ["Upcoming", "In Progress", "Completed", "Cancelled"];

const inputCls = "w-full rounded-xl border border-slate-300 px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60 focus:border-brand-teal";

const STATUS_COLORS = {
  "Upcoming": "border-l-blue-500",
  "In Progress": "border-l-amber-500",
  "Completed": "border-l-green-600",
  "Cancelled": "border-l-red-500 line-through opacity-60",
};

function startOfWeek(d) {
  const dt = new Date(d);
  dt.setDate(dt.getDate() - dt.getDay());
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function fmt(d) {
  return d.toISOString().slice(0, 10);
}

const emptyVisit = { patient_id: "", caregiver_id: "", date: "", start_time: "", end_time: "", care_type: "Personal Care", instructions: "", notes: "", status: "Upcoming" };

export default function Schedule() {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [visits, setVisits] = useState([]);
  const [patients, setPatients] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [filters, setFilters] = useState({ patient_id: "", caregiver_id: "", status: "" });
  const [modal, setModal] = useState(null); // { mode, id? }
  const [form, setForm] = useState(emptyVisit);
  const [saving, setSaving] = useState(false);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  }), [weekStart]);

  const loadVisits = () => {
    const params = new URLSearchParams({ date_from: fmt(weekStart), date_to: fmt(weekEnd) });
    if (filters.patient_id) params.set("patient_id", filters.patient_id);
    if (filters.caregiver_id) params.set("caregiver_id", filters.caregiver_id);
    if (filters.status) params.set("status", filters.status);
    api.get(`/admin/visits?${params}`).then((r) => setVisits(r.data)).catch(() => setVisits([]));
  };

  useEffect(() => {
    api.get("/admin/patients").then((r) => setPatients(r.data)).catch(() => {});
    api.get("/admin/caregivers").then((r) => setCaregivers(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadVisits(); }, [weekStart, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = (day) => {
    setForm({ ...emptyVisit, date: fmt(day) });
    setModal({ mode: "create" });
  };

  const openEdit = (v) => {
    setForm({ patient_id: v.patient_id, caregiver_id: v.caregiver_id || "", date: v.date, start_time: v.start_time, end_time: v.end_time, care_type: v.care_type, instructions: v.instructions || "", notes: v.notes || "", status: v.status });
    setModal({ mode: "edit", id: v.id, visit: v });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modal.mode === "create") {
        await api.post("/admin/visits", form);
        toast.success("Visit scheduled and assigned.");
      } else {
        await api.patch(`/admin/visits/${modal.id}`, form);
        toast.success("Visit updated.");
      }
      setModal(null);
      loadVisits();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const shiftWeek = (n) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + n * 7);
    setWeekStart(d);
  };

  return (
    <div data-testid="admin-schedule">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-brand-navy">Schedule / Visits</h1>
          <p className="mt-2 text-[15px] text-slate-600">Create visits, assign caregivers, and manage the weekly care schedule. Assigned visits appear instantly in the caregiver's portal.</p>
        </div>
        <button onClick={() => { setForm(emptyVisit); setModal({ mode: "create" }); }} data-testid="new-visit-button" className="inline-flex items-center gap-2 rounded-full bg-brand-teal px-6 py-3 text-[15px] font-bold text-white hover:bg-brand-navy transition-colors duration-300 active:scale-95">
          <Plus className="h-4 w-4" /> New Visit
        </button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button onClick={() => shiftWeek(-1)} data-testid="prev-week" aria-label="Previous week" className="p-2.5 rounded-xl border border-slate-300 bg-white hover:bg-brand-mist"><ChevronLeft className="h-5 w-5" /></button>
        <button onClick={() => setWeekStart(startOfWeek(new Date()))} data-testid="this-week" className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-brand-navy hover:bg-brand-mist">This Week</button>
        <button onClick={() => shiftWeek(1)} data-testid="next-week" aria-label="Next week" className="p-2.5 rounded-xl border border-slate-300 bg-white hover:bg-brand-mist"><ChevronRight className="h-5 w-5" /></button>
        <p data-testid="week-range" className="text-sm font-bold text-brand-navy">
          {weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – {weekEnd.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
        </p>
        <div className="flex flex-wrap gap-3 ml-auto">
          <select data-testid="filter-patient" value={filters.patient_id} onChange={(e) => setFilters({ ...filters, patient_id: e.target.value })} className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/60">
            <option value="">All patients</option>
            {patients.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
          </select>
          <select data-testid="filter-caregiver" value={filters.caregiver_id} onChange={(e) => setFilters({ ...filters, caregiver_id: e.target.value })} className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/60">
            <option value="">All caregivers</option>
            {caregivers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/60">
            <option value="">All statuses</option>
            {VISIT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto pb-2">
        <div className="grid grid-cols-7 gap-2 min-w-[980px]">
          {days.map((d) => {
            const key = fmt(d);
            const dayVisits = visits.filter((v) => v.date === key);
            const isToday = key === fmt(new Date());
            return (
              <div key={key} data-testid={`day-${key}`} className={`rounded-2xl border ${isToday ? "border-brand-teal" : "border-slate-200"} bg-white min-h-[180px] flex flex-col`}>
                <div className={`px-3 py-2.5 border-b ${isToday ? "border-brand-teal/30 bg-brand-teal/5" : "border-slate-100"} rounded-t-2xl`}>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{d.toLocaleDateString(undefined, { weekday: "short" })}</p>
                  <p className={`text-lg font-display font-semibold ${isToday ? "text-brand-teal" : "text-brand-navy"}`}>{d.getDate()}</p>
                </div>
                <div className="p-2 space-y-2 flex-1">
                  {dayVisits.map((v) => (
                    <button
                      key={v.id}
                      data-testid={`visit-chip-${v.id}`}
                      onClick={() => openEdit(v)}
                      className={`w-full text-left rounded-lg border-l-4 ${STATUS_COLORS[v.status] || "border-l-slate-300"} bg-brand-paper px-2.5 py-2 hover:shadow-md transition-shadow duration-200`}
                    >
                      <p className="text-xs font-bold text-brand-navy">{v.start_time} – {v.end_time}</p>
                      <p className="text-xs font-semibold text-slate-700 truncate">{v.patient_name}</p>
                      <p className="text-[11px] text-slate-500 truncate">{v.caregiver_name} · {v.care_type}</p>
                    </button>
                  ))}
                  <button onClick={() => openCreate(d)} data-testid={`add-visit-${key}`} className="w-full rounded-lg border border-dashed border-slate-300 py-1.5 text-xs font-bold text-slate-400 hover:text-brand-teal hover:border-brand-teal/50 transition-colors duration-200">
                    + Add
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/60 p-4" onClick={() => setModal(null)}>
          <div data-testid="visit-modal" className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 md:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl text-brand-navy">{modal.mode === "create" ? "Create Visit" : "Update Visit"}</h2>
              <button onClick={() => setModal(null)} data-testid="visit-modal-close" aria-label="Close" className="p-2 rounded-lg hover:bg-brand-mist"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={save} className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="visit-patient" className="block text-sm font-bold text-brand-navy">Patient *</label>
                <select id="visit-patient" data-testid="visit-patient" required value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })} className={`mt-2 ${inputCls}`}>
                  <option value="">Select patient</option>
                  {patients.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="visit-caregiver" className="block text-sm font-bold text-brand-navy">Assign Caregiver</label>
                <select id="visit-caregiver" data-testid="visit-caregiver" value={form.caregiver_id} onChange={(e) => setForm({ ...form, caregiver_id: e.target.value })} className={`mt-2 ${inputCls}`}>
                  <option value="">Unassigned</option>
                  {caregivers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="visit-date" className="block text-sm font-bold text-brand-navy">Visit Date *</label>
                <input id="visit-date" data-testid="visit-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <label htmlFor="visit-care-type" className="block text-sm font-bold text-brand-navy">Type of Care *</label>
                <select id="visit-care-type" data-testid="visit-care-type" value={form.care_type} onChange={(e) => setForm({ ...form, care_type: e.target.value })} className={`mt-2 ${inputCls}`}>
                  {CARE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="visit-start" className="block text-sm font-bold text-brand-navy">Start Time *</label>
                <input id="visit-start" data-testid="visit-start" type="time" required value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <label htmlFor="visit-end" className="block text-sm font-bold text-brand-navy">End Time *</label>
                <input id="visit-end" data-testid="visit-end" type="time" required value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className={`mt-2 ${inputCls}`} />
              </div>
              {modal.mode === "edit" && (
                <div>
                  <label htmlFor="visit-status" className="block text-sm font-bold text-brand-navy">Visit Status</label>
                  <select id="visit-status" data-testid="visit-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={`mt-2 ${inputCls}`}>
                    {VISIT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
              <div className="sm:col-span-2">
                <label htmlFor="visit-instructions" className="block text-sm font-bold text-brand-navy">Visit Instructions</label>
                <textarea id="visit-instructions" data-testid="visit-instructions" rows={3} value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} className={`mt-2 ${inputCls}`} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="visit-notes" className="block text-sm font-bold text-brand-navy">Important Care Notes</label>
                <textarea id="visit-notes" data-testid="visit-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`mt-2 ${inputCls}`} />
              </div>
              {modal.mode === "edit" && modal.visit?.status === "Completed" && (
                <div className="sm:col-span-2 rounded-xl bg-green-50 border border-green-200 p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-green-800">Completion Note from Caregiver</p>
                  <p className="mt-1 text-sm text-slate-600">Completed by {modal.visit.completed_by || "caregiver"}{modal.visit.completed_at ? ` on ${new Date(modal.visit.completed_at).toLocaleString()}` : ""}</p>
                  {modal.visit.completion_note && <p className="mt-2 text-[15px] text-slate-700 whitespace-pre-wrap">{modal.visit.completion_note}</p>}
                </div>
              )}
              <div className="sm:col-span-2">
                <button type="submit" data-testid="visit-save" disabled={saving} className="rounded-full bg-brand-teal px-6 py-3 text-[15px] font-bold text-white hover:bg-brand-navy transition-colors duration-300 active:scale-95 disabled:opacity-60">
                  {saving ? "Saving…" : modal.mode === "create" ? "Save Visit" : "Update Visit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
